<script setup>
// Inspector dos nós de ferramenta (tool_*) que o agente pode usar: explica o
// que a ferramenta faz e os parâmetros que ela usa. A maioria não precisa de
// setup (o agente decide quando chamar e com quais dados). tool_escalate tem
// inspector próprio (destino configurável).
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { NODE_META } from './nodeMeta';

const props = defineProps({ data: { type: Object, required: true } });
const emit = defineEmits(['update', 'remove', 'close']);
const { t } = useI18n();

const meta = computed(() => NODE_META[props.data.kind] || {});
const color = computed(() => props.data.color || meta.value.color || '#9aa0a6');
const tint = hex => `${hex}1f`;

// Descrição e parâmetros por ferramenta (i18n). Chave dinâmica por kind.
const paramsLabel = computed(
  () => `${t('AI_AGENTS.FLOW.TOOLS.PARAMS_LABEL')}:`
);
const desc = computed(() => t(`AI_AGENTS.FLOW.TOOLS.${props.data.kind}.DESC`));
const params = computed(() => {
  const key = `AI_AGENTS.FLOW.TOOLS.${props.data.kind}.PARAMS`;
  const val = t(key);
  return val === key ? '' : val;
});
</script>

<template>
  <div class="p-4">
    <div class="mb-3 flex items-center justify-between">
      <div class="flex items-center gap-2.5">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-xl"
          :style="{ backgroundColor: tint(color), color }"
        >
          <span :class="meta.icon || 'i-lucide-wrench'" class="text-base" />
        </span>
        <h3 class="text-sm font-semibold text-n-slate-12">
          {{ data.label || meta.label }}
        </h3>
      </div>
      <button class="i-lucide-x text-n-slate-10" @click="emit('close')" />
    </div>

    <div class="mb-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3">
      <p class="text-sm text-n-slate-12">{{ desc }}</p>
      <p v-if="params" class="mt-2 text-xs text-n-slate-10">
        <span class="font-medium">{{ paramsLabel }}</span> {{ params }}
      </p>
    </div>

    <!-- Liga/desliga a ferramenta sem precisar removê-la do canvas. -->
    <label
      class="mb-3 flex cursor-pointer items-center justify-between rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2.5"
    >
      <div>
        <span class="block text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.FLOW.TOOLS.ENABLED') }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{ t('AI_AGENTS.FLOW.TOOLS.ENABLED_HINT') }}
        </span>
      </div>
      <input
        type="checkbox"
        class="size-4 shrink-0 accent-[#4285f4]"
        :checked="data.enabled !== false"
        @change="emit('update', { enabled: $event.target.checked })"
      />
    </label>

    <label class="mb-1 block text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.SUBTITLE') }}
    </label>
    <input
      :value="data.subtitle"
      :placeholder="t('AI_AGENTS.FLOW.OPTIONAL')"
      class="mb-3 w-full rounded-lg border border-n-weak bg-n-solid-1 px-2.5 py-1.5 text-sm"
      @input="emit('update', { subtitle: $event.target.value })"
    />

    <p class="text-xs text-n-slate-10">
      {{ t('AI_AGENTS.FLOW.TOOLS.NO_SETUP') }}
    </p>

    <button
      class="mt-4 w-full rounded-lg border border-n-ruby-7 py-2 text-sm font-medium text-n-ruby-9 hover:bg-n-ruby-3"
      @click="emit('remove')"
    >
      {{ t('AI_AGENTS.FLOW.REMOVE_NODE') }}
    </button>
  </div>
</template>
